import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ChevronRight, Play, CheckCircle, Clock, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AnimatedGroup } from '@/components/ui/animated-group';
import { Marquee } from '@/components/ui/marquee';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';
import { DemoModal } from '@/components/DemoModal';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

const transitionVariants = {
    item: {
        hidden: {
            opacity: 0,
            filter: 'blur(12px)',
            y: 12,
        },
        visible: {
            opacity: 1,
            filter: 'blur(0px)',
            y: 0,
            transition: {
                type: 'spring' as const,
                bounce: 0.3,
                duration: 1.5,
            },
        },
    },
} as const;

// CRO: Proposta de valor clara e específica para dor técnica
const valuePropositions = [
    { icon: Clock, text: 'Economize 15h/semana' },
    { icon: Shield, text: '100% automático' },
];

export function HeroSectionObrim() {
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const [showDemoModal, setShowDemoModal] = useState(false);

    // CRO: Urgency Banner State
    const [vagasRestantes] = useState(47);

    return (
        <>
            <main className="overflow-hidden">
                {/* Background Aurora Effects - Simplificado no mobile para performance */}
                {!isMobile && (
                    <div
                        aria-hidden
                        className="z-[2] absolute inset-0 pointer-events-none isolate opacity-30 lg:opacity-50 contain-strict block">
                        <div className="w-[35rem] h-[80rem] -translate-y-[350px] absolute left-0 top-0 -rotate-45 rounded-full bg-[radial-gradient(68.54%_68.72%_at_55.02%_31.46%,hsl(168_76%_42%/0.15)_0,hsl(168_76%_42%/0.05)_50%,transparent_80%)]" />
                        <div className="h-[80rem] absolute left-0 top-0 w-56 -rotate-45 rounded-full bg-[radial-gradient(50%_50%_at_50%_50%,hsl(168_76%_42%/0.1)_0,hsl(168_76%_42%/0.02)_80%,transparent_100%)] [translate:5%_-50%]" />
                    </div>
                )}

                <section>
                    <div className="relative pt-20 md:pt-36">
                        {/* Background Image - Desktop only */}
                        {!isMobile && (
                            <AnimatedGroup
                                variants={{
                                    container: {
                                        visible: {
                                            transition: {
                                                delayChildren: 1,
                                            },
                                        },
                                    },
                                    item: {
                                        hidden: {
                                            opacity: 0,
                                            y: 20,
                                        },
                                        visible: {
                                            opacity: 1,
                                            y: 0,
                                            transition: {
                                                type: 'spring' as const,
                                                bounce: 0.3,
                                                duration: 2,
                                            },
                                        },
                                    },
                                }}
                                className="absolute inset-0 -z-20">
                                <div className="absolute inset-x-0 top-32 -z-20 hidden dark:block h-[600px] bg-gradient-to-b from-primary/5 via-primary/10 to-transparent" />
                            </AnimatedGroup>
                        )}

                        <div aria-hidden className="absolute inset-0 -z-10 size-full [background:radial-gradient(125%_125%_at_50%_100%,transparent_0%,var(--background)_75%)]" />

                        <div className="mx-auto max-w-7xl px-4 sm:px-6">
                            <div className="text-center sm:mx-auto lg:mr-auto lg:mt-0">
                                {/* CRO: Urgency Banner - Otimizado para mobile */}
                                <div className="z-10 flex items-center justify-center mb-4 md:mb-6">
                                    <div className="px-3 py-1.5 md:px-4 md:py-2 rounded-lg bg-red-100/80 border border-red-200 dark:bg-red-900/20 dark:border-red-900/50 backdrop-blur-sm shadow-sm">
                                        <p className="text-xs sm:text-sm font-bold text-red-700 dark:text-red-400 flex items-center gap-1.5 flex-wrap justify-center">
                                            <span className="text-base">🔥</span> 
                                            <span className="hidden sm:inline">Próximos 100 usuários:</span>
                                            <span className="sm:hidden">100 usuários:</span>
                                            Desconto de 30%
                                        </p>
                                    </div>
                                </div>

                                {/* CRO: Headline - MOBILE FIRST: Emocional e direto */}
                                <h1 className="mt-4 md:mt-6 max-w-4xl mx-auto text-balance font-bold tracking-tight text-foreground leading-[1.1]">
                                    {/* Mobile: Headline emocional (dor imediata) */}
                                    <span className="block md:hidden text-[1.75rem] sm:text-3xl">
                                        Chega de{' '}
                                        <span className="text-primary">planilha.</span>
                                        <br />
                                        <span className="text-muted-foreground text-xl sm:text-2xl mt-1 block">
                                            Controle sua obra em 5 min.
                                        </span>
                                    </span>
                                    
                                    {/* Desktop: Headline completa */}
                                    <span className="hidden md:block text-5xl lg:text-6xl xl:text-[4.5rem]">
                                        Chega de{' '}
                                        <span className="relative inline-block text-primary">
                                            Planilha.
                                            <span className="absolute inset-0 blur-xl bg-primary/20 -z-10 scale-125" />
                                        </span>
                                        <br />
                                        <span className="text-muted-foreground block mt-2 text-4xl lg:text-5xl xl:text-[3.5rem]">
                                            Controle sua obra em 5 minutos.
                                        </span>
                                    </span>
                                </h1>

                                {/* CRO: Subheadline - Benefício claro e sem atrito */}
                                <p className="mx-auto mt-4 md:mt-6 lg:mt-8 max-w-xl md:max-w-2xl text-balance text-sm sm:text-base md:text-lg text-muted-foreground leading-relaxed px-2">
                                    {isMobile ? (
                                        <><strong className="text-foreground">7 dias grátis.</strong> Sem cartão. Sem enrolação.</>
                                    ) : (
                                        <>Cronograma, orçamento e equipe em um só lugar. <strong className="text-foreground">7 dias grátis. Sem cartão.</strong></>
                                    )}
                                </p>

                                {/* CRO: CTAs - Mobile: Full width, 56px altura */}
                                <div className="mt-6 md:mt-8 lg:mt-10 flex flex-col items-center justify-center gap-3 md:gap-4 w-full max-w-md mx-auto px-2">

                                    {/* Primary CTA - Mobile Optimized: Full width, taller */}
                                    <Button
                                        size="lg"
                                        className="w-full h-14 min-h-[56px] text-base md:text-lg font-bold rounded-xl shadow-lg shadow-primary/20 bg-primary hover:bg-primary/90 transition-all hover:scale-[1.02] active:scale-[0.98]"
                                        onClick={() => navigate('/register')}
                                    >
                                        {isMobile ? 'Começar Grátis' : 'Teste Grátis por 7 Dias'}
                                        <ArrowRight className="ml-2 w-5 h-5" />
                                    </Button>

                                    {/* Secondary CTA */}
                                    <button
                                        onClick={() => setShowDemoModal(true)}
                                        className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-muted/50 min-h-[44px]"
                                    >
                                        <Play className="w-4 h-4 fill-current opacity-80" />
                                        Ver Demo (2 min)
                                    </button>
                                </div>

                                {/* CRO: Prova Social ABOVE THE FOLD - números + depoimento */}
                                <div className="mt-6 md:mt-8 space-y-4">
                                    {/* Stats rápidos - Mobile first */}
                                    <div className="flex items-center justify-center gap-4 md:gap-6">
                                        <div className="text-center">
                                            <p className="text-lg md:text-xl font-bold text-foreground">500+</p>
                                            <p className="text-[10px] md:text-xs text-muted-foreground">engenheiros</p>
                                        </div>
                                        <div className="h-8 w-px bg-border" />
                                        <div className="text-center">
                                            <p className="text-lg md:text-xl font-bold text-primary flex items-center justify-center gap-1">
                                                4.8
                                                <span className="text-yellow-500 text-sm">★</span>
                                            </p>
                                            <p className="text-[10px] md:text-xs text-muted-foreground">avaliação</p>
                                        </div>
                                        <div className="h-8 w-px bg-border" />
                                        <div className="text-center">
                                            <p className="text-lg md:text-xl font-bold text-foreground">R$50M+</p>
                                            <p className="text-[10px] md:text-xs text-muted-foreground">gerenciados</p>
                                        </div>
                                    </div>
                                    
                                    {/* Depoimento curto */}
                                    <div className="inline-flex flex-col sm:flex-row items-center gap-2 sm:gap-3 bg-background/50 backdrop-blur-sm border border-border/50 rounded-xl md:rounded-2xl p-2.5 sm:p-3 sm:pr-6 shadow-sm max-w-lg">
                                        <div className="relative">
                                            <div className="w-9 h-9 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-primary to-violet-500 flex items-center justify-center text-white font-bold text-sm sm:text-lg">
                                                JO
                                            </div>
                                        </div>
                                        <div className="text-center sm:text-left">
                                            <p className="text-xs sm:text-sm font-medium italic text-muted-foreground leading-snug">
                                                {isMobile ? (
                                                    <>"Economizei <span className="text-primary font-semibold">20h/mês</span>"</>
                                                ) : (
                                                    <>"Economizei <span className="text-primary font-semibold">20 horas por mês</span>. Descobri um erro que me custaria R$15.000."</>
                                                )}
                                            </p>
                                            <p className="text-[10px] sm:text-xs text-muted-foreground mt-0.5">
                                                <span className="font-semibold text-foreground">João Oliveira</span> • Eng. Civil
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* CRO: Trust signals - Horizontal no mobile */}
                                <div className="mt-5 md:mt-6 flex items-center justify-center gap-3 md:gap-6 text-[11px] sm:text-xs md:text-sm text-muted-foreground flex-wrap">
                                    <div className="flex items-center gap-1">
                                        <CheckCircle className="w-3.5 h-3.5 md:w-4 md:h-4 text-primary" />
                                        <span>Sem cartão</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <CheckCircle className="w-3.5 h-3.5 md:w-4 md:h-4 text-primary" />
                                        <span>Cancele quando quiser</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <CheckCircle className="w-3.5 h-3.5 md:w-4 md:h-4 text-primary" />
                                        <span>Suporte WhatsApp</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Dashboard Mockup - CRO: Prova visual do produto */}
                        <AnimatedGroup
                            variants={{
                                container: {
                                    visible: {
                                        transition: {
                                            staggerChildren: 0.05,
                                            delayChildren: 0.75,
                                        },
                                    },
                                },
                                ...transitionVariants,
                            }}>
                            <div className="relative mt-8 sm:mt-12 md:mt-20 overflow-hidden px-2 sm:mr-0 -mr-0 sm:-mr-0">
                                <div
                                    aria-hidden
                                    className="bg-gradient-to-b to-background absolute inset-0 z-10 from-transparent from-35%"
                                />
                                <div className="inset-shadow-2xs ring-background dark:inset-shadow-white/20 bg-background relative mx-auto max-w-6xl overflow-hidden rounded-2xl border p-4 shadow-lg shadow-zinc-950/15 ring-1">
                                    {/* Dashboard Content */}
                                    <div className="relative aspect-[16/10] lg:aspect-[16/9] bg-gradient-to-br from-muted/20 to-muted/5 rounded-xl overflow-hidden">
                                        <div className="absolute inset-0 p-4 sm:p-6 lg:p-8">
                                            {/* Browser Chrome */}
                                            <div className="flex items-center gap-2 mb-4">
                                                <div className="flex gap-1.5">
                                                    <div className="w-3 h-3 rounded-full bg-red-500/70" />
                                                    <div className="w-3 h-3 rounded-full bg-yellow-500/70" />
                                                    <div className="w-3 h-3 rounded-full bg-green-500/70" />
                                                </div>
                                                <div className="flex-1 flex justify-center">
                                                    <span className="text-xs text-muted-foreground bg-muted/50 px-3 py-1 rounded-full">🔒 app.obrim.com.br</span>
                                                </div>
                                            </div>

                                            {/* Header */}
                                            <div className="flex items-center justify-between mb-4 lg:mb-6">
                                                <div>
                                                    <h3 className="text-sm sm:text-base lg:text-lg font-bold text-foreground">Boa tarde, Samuel!</h3>
                                                    <p className="text-xs sm:text-sm text-muted-foreground">Controle total dos seus projetos</p>
                                                </div>
                                                <div className="hidden sm:block px-3 py-2 rounded-lg border border-border bg-background text-xs lg:text-sm text-foreground">
                                                    + Novo Projeto
                                                </div>
                                            </div>

                                            {/* Stats Cards */}
                                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4 lg:mb-6">
                                                <div className="bg-background/80 rounded-xl p-3 lg:p-4 border border-border/50">
                                                    <span className="text-[10px] lg:text-xs text-muted-foreground">Receita Mensal</span>
                                                    <p className="text-base lg:text-xl font-bold text-foreground">R$ 127k</p>
                                                    <span className="text-[10px] lg:text-xs text-green-500">+18.5%</span>
                                                </div>
                                                <div className="bg-background/80 rounded-xl p-3 lg:p-4 border border-border/50">
                                                    <span className="text-[10px] lg:text-xs text-muted-foreground">Obras Ativas</span>
                                                    <p className="text-base lg:text-xl font-bold text-foreground">12</p>
                                                    <span className="text-[10px] lg:text-xs text-primary">Em andamento</span>
                                                </div>
                                                <div className="hidden lg:block bg-background/80 rounded-xl p-4 border border-border/50">
                                                    <span className="text-xs text-muted-foreground">Progresso</span>
                                                    <p className="text-xl font-bold text-foreground">67%</p>
                                                    <span className="text-xs text-muted-foreground">No prazo</span>
                                                </div>
                                                <div className="hidden lg:block bg-background/80 rounded-xl p-4 border border-border/50">
                                                    <span className="text-xs text-muted-foreground">Health Score</span>
                                                    <p className="text-xl font-bold text-primary">92</p>
                                                    <span className="text-xs text-green-500">Excelente</span>
                                                </div>
                                            </div>

                                            {/* Chart */}
                                            <div className="bg-background/60 rounded-xl p-3 lg:p-4 border border-border/50 h-20 sm:h-24 lg:h-32 flex items-end justify-between gap-1">
                                                {[40, 65, 45, 80, 60, 90, 75, 85, 70, 95, 80, 88].map((height, i) => (
                                                    <motion.div
                                                        key={i}
                                                        initial={{ height: 0 }}
                                                        animate={{ height: `${height}%` }}
                                                        transition={{ duration: 0.4, delay: 1.2 + i * 0.05 }}
                                                        className="flex-1 bg-gradient-to-t from-primary/30 to-primary rounded-t-sm"
                                                    />
                                                ))}
                                            </div>
                                        </div>

                                        {/* AI Scan Effect */}
                                        <motion.div
                                            className="absolute inset-0 bg-gradient-to-b from-primary/5 via-primary/10 to-transparent pointer-events-none"
                                            animate={{ y: ['-100%', '200%'] }}
                                            transition={{ duration: 3, repeat: Infinity, repeatDelay: 2, ease: 'linear' }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </AnimatedGroup>

                        {/* SPIN SELLING HOOKS - Elementos flutuantes (Problem -> Solution) */}
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 1, duration: 0.8 }}
                            className="absolute left-4 lg:left-20 top-[60%] lg:top-[50%] z-20 hidden md:flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 dark:bg-black/20 p-4 shadow-2xl backdrop-blur-xl max-w-[220px] lg:max-w-xs"
                        >
                            <div className="rounded-full bg-red-500/20 p-2 shrink-0">
                                <AlertTriangle className="h-5 w-5 text-red-500" />
                            </div>
                            <div className="flex flex-col">
                                <span className="text-xs font-semibold text-foreground">Risco de Atraso Detectado</span>
                                <span className="text-[10px] text-muted-foreground leading-tight">Material da Obra Alpha não chegou. Impacto: 3 dias.</span>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 2.5, duration: 0.8 }}
                            className="absolute right-4 lg:right-20 top-[75%] lg:top-[65%] z-20 hidden md:flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 dark:bg-black/20 p-4 shadow-2xl backdrop-blur-xl max-w-[220px] lg:max-w-xs"
                        >
                            <div className="rounded-full bg-primary/20 p-2 shrink-0">
                                <CheckCircle2 className="h-5 w-5 text-primary" />
                            </div>
                            <div className="flex flex-col">
                                <span className="text-xs font-semibold text-foreground">Cronograma Reajustado</span>
                                <span className="text-[10px] text-muted-foreground leading-tight">Tarefas realocadas automaticamente. Prazo mantido.</span>
                            </div>
                        </motion.div>

                        {/* Mobile SPIN Selling - Static Cards below image */}
                        {/* Mobile SPIN Selling - Static Cards below image */}
                        <div className="mt-6 flex flex-col gap-3 md:hidden px-4 animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-700 fill-mode-both">
                            <div className="flex items-center gap-3 rounded-xl border border-border/50 bg-background/50 p-3 backdrop-blur-sm">
                                <div className="rounded-full bg-red-500/10 p-2 shrink-0">
                                    <AlertTriangle className="h-4 w-4 text-red-500" />
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-xs font-semibold text-foreground">Risco de Atraso Detectado</span>
                                    <span className="text-[10px] text-muted-foreground">Material da Obra Alpha não chegou.</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 rounded-xl border border-border/50 bg-background/50 p-3 backdrop-blur-sm">
                                <div className="rounded-full bg-primary/10 p-2 shrink-0">
                                    <CheckCircle2 className="h-4 w-4 text-primary" />
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-xs font-semibold text-foreground">Cronograma Reajustado</span>
                                    <span className="text-[10px] text-muted-foreground">Tarefas realocadas automaticamente.</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </section >

                {/* CRO: Social Proof Section - Logos de clientes/parceiros */}
                < section className="bg-background pb-12 pt-12 md:pb-20 md:pt-16" >
                    <div className="group relative m-auto max-w-5xl px-6">
                        <div className="absolute inset-0 z-10 flex scale-95 items-center justify-center opacity-0 duration-500 group-hover:scale-100 group-hover:opacity-100">
                            <button
                                onClick={() => navigate('/register')}
                                className="block text-sm duration-150 hover:opacity-75 bg-background/90 backdrop-blur-sm px-4 py-2 rounded-full border border-border shadow-lg">
                                <span>Junte-se a +150 engenheiros</span>
                                <ChevronRight className="ml-1 inline-block size-3" />
                            </button>
                        </div>

                        <motion.p
                            initial={{ opacity: 0, y: 10 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className="text-center text-sm text-muted-foreground font-medium uppercase tracking-wider mb-8"
                        >
                            Utilizado por engenheiros em todo o Brasil
                        </motion.p>

                        <div className="mask-gradient-to-r relative w-full overflow-hidden">
                            <Marquee speed={40} className="py-4">
                                {[
                                    { name: 'Construtora Alpha', initials: 'CA' },
                                    { name: 'Engenharia Beta', initials: 'EB' },
                                    { name: 'Arquitetura Nova', initials: 'AN' },
                                    { name: 'Projetos Sigma', initials: 'PS' },
                                    { name: 'Obras Prime', initials: 'OP' },
                                    { name: 'BuildTech', initials: 'BT' },
                                    { name: 'Estrutural Pro', initials: 'EP' },
                                    { name: 'Mega Construtora', initials: 'MC' },
                                ].map((logo, index) => (
                                    <div
                                        key={index}
                                        className="mx-6 flex items-center justify-center gap-2 opacity-50 hover:opacity-100 transition-opacity duration-300"
                                    >
                                        <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center text-xs font-bold text-muted-foreground">
                                            {logo.initials}
                                        </div>
                                        <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">
                                            {logo.name}
                                        </span>
                                    </div>
                                ))}
                            </Marquee>
                        </div>

                        {/* Stats rápidos - CRO: Números específicos geram confiança */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.3 }}
                            className="mt-10 lg:mt-12"
                        >
                            <div className="flex flex-wrap justify-center gap-8 lg:gap-16 text-center">
                                <div>
                                    <p className="text-2xl lg:text-3xl font-bold text-foreground">150+</p>
                                    <p className="text-xs sm:text-sm text-muted-foreground">Engenheiros ativos</p>
                                </div>
                                <div className="hidden sm:block h-12 w-px bg-border" />
                                <div>
                                    <p className="text-2xl lg:text-3xl font-bold text-foreground">500+</p>
                                    <p className="text-xs sm:text-sm text-muted-foreground">Obras gerenciadas</p>
                                </div>
                                <div className="hidden sm:block h-12 w-px bg-border" />
                                <div>
                                    <p className="text-2xl lg:text-3xl font-bold text-primary">R$ 50M+</p>
                                    <p className="text-xs sm:text-sm text-muted-foreground">Em projetos controlados</p>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </section >
            </main >

            <DemoModal open={showDemoModal} onOpenChange={setShowDemoModal} />
        </>
    );
}

export default HeroSectionObrim;
